import { NormalizedCacheObject } from 'apollo-boost';
import { ExecutionResult } from 'graphql';

import {
    createRetargetingMutation,
    deleteRetargetings,
    resumeRetargetings,
    retargetingCommonPriceEditorMutation,
    suspendRetargetings,
    updateRetargetingMutation
} from '../../graphql/mutations/grid/retargeting';
import { deleteRetargetingConditions } from '../../graphql/mutations/grid/retargetings';
import { mapRulesGroupsToApiFormat } from '../../mappers/retargeting/retargeting';
import {
    CreateRetargetingConditionsMutation,
    DeleteRetargetingConditionsMutation,
    DeleteRetargetingsMutation,
    GdRetargetingConditionTypeInput,
    ResumeRetargetingsMutation,
    RetargetingCommonPriceEditorMutation,
    SuspendRetargetingsMutation,
    UpdateRetargetingConditionsMutation
} from '../../typings/graphql-grid';
import { RetargetingConditionData } from '../../typings/retargeting';
import { DNAApolloClient } from '../DNAApolloClient/DNAApolloClient';

export class RetargetingMutationExecutor {
    constructor(private client: DNAApolloClient<NormalizedCacheObject>) {}

    public async updateRetargetingCondition(
        conditionData: RetargetingConditionData
    ): Promise<ExecutionResult<UpdateRetargetingConditionsMutation>> {
        const { name, description, id, rulesGroups } = conditionData;

        return this.client.mutate<UpdateRetargetingConditionsMutation>({
            mutation: updateRetargetingMutation,
            variables: {
                input: {
                    retargetingConditions: [
                        {
                            name,
                            description,
                            retargetingConditionId: Number(id),
                            type: GdRetargetingConditionTypeInput.METRIKA_GOALS,
                            conditionRules: mapRulesGroupsToApiFormat(rulesGroups)
                        }
                    ]
                }
            }
        });
    }

    public async createRetargetingCondition(
        conditionData: RetargetingConditionData
    ): Promise<ExecutionResult<CreateRetargetingConditionsMutation>> {
        const { name, description, rulesGroups } = conditionData;
        return this.client.mutate<CreateRetargetingConditionsMutation>({
            mutation: createRetargetingMutation,
            variables: {
                input: {
                    retargetingConditions: [
                        {
                            name,
                            description,
                            type: GdRetargetingConditionTypeInput.METRIKA_GOALS,
                            conditionRules: mapRulesGroupsToApiFormat(rulesGroups)
                        }
                    ]
                }
            }
        });
    }

    public async deleteRetargetingConditions(
        login: string,
        retargetingConditionIds: string[]
    ): Promise<ExecutionResult<DeleteRetargetingConditionsMutation>> {
        return this.client.mutate<DeleteRetargetingConditionsMutation>({
            mutation: deleteRetargetingConditions,
            variables: {
                input: {
                    retargetingConditionIds
                },
                login
            }
        });
    }

    public async resumeRetargetings(retargetingIds: string[], login: string): Promise<ResumeRetargetingsMutation> {
        const response = await this.client.awaitMutate<ResumeRetargetingsMutation>({
            mutation: resumeRetargetings,
            variables: {
                input: {
                    retargetingIds
                },
                login
            },
            login
        });

        return response.data as ResumeRetargetingsMutation;
    }

    public async suspendRetargetings(retargetingIds: string[], login: string): Promise<SuspendRetargetingsMutation> {
        const response = await this.client.awaitMutate<SuspendRetargetingsMutation>({
            mutation: suspendRetargetings,
            variables: {
                input: {
                    retargetingIds
                },
                login
            },
            login
        });

        return response.data as SuspendRetargetingsMutation;
    }

    public async retargetingCommonPriceEditorMutation(
        login: string,
        commonPriceValue: number,
        retargetingIds: string[]
    ): Promise<ExecutionResult<RetargetingCommonPriceEditorMutation>> {
        return this.client.awaitMutate<RetargetingCommonPriceEditorMutation>({
            mutation: retargetingCommonPriceEditorMutation,
            login,
            variables: {
                login,
                input: {
                    exactPriceContext: commonPriceValue,
                    showConditionIds: retargetingIds
                }
            }
        });
    }

    public async deleteRetargetings(retargetingIds: string[], login: string): Promise<DeleteRetargetingsMutation> {
        const response = await this.client.awaitMutate<DeleteRetargetingsMutation>({
            mutation: deleteRetargetings,
            variables: {
                input: {
                    retargetingIds
                },
                login
            },
            login
        });

        return response.data as DeleteRetargetingsMutation;
    }
}
